import { Merchant } from "../types/Merchant";
import { Currency } from "../types/Payment";
import { PaymentPlanType, Price, RecurringInterval } from "../types/Product";
import { amountAsCurrency, toString } from "./currency";

// TODO: this will most likely break when recurring_interval is not month
export const lowestMonthlyPrice = (
  prices: Price[],
  currency: Currency
): string => {
  const pricesMonthly = prices.map(
    (price) => price.amount / normalizeInterval(price)
  );

  const minPrice = Math.min(...pricesMonthly);

  return toString({ amount: minPrice, currency: currency });
};

export const lowestPriceAsCurrency = (
  prices: Price[],
  currency: Currency
): string => {
  const pricesMonthly = prices.map((price) => price.amount);

  const minPrice = Math.min(...pricesMonthly);

  return toString({ amount: minPrice, currency: currency });
};

export const lowestPricePerInstallment = (prices: Price[]): Price => {
  const lowestPrice = prices
    .filter((x) => x.payment_plan_type === PaymentPlanType.INSTALLMENT)
    .sort(
      (a, b) =>
        a.amount / a.installments_count - b.amount / b.installments_count
    )[0];

  return lowestPrice;
};

export const priceLabel = (price: Price, prefix?: string): string => {
  return price.name || priceIntervalText(price, prefix);
};

export const priceIntervalText = (price: Price, prefix?: string) => {
  const { recurring_interval, recurring_interval_count } = price;

  let label = prefix ? `${prefix} cada ` : "cada ";

  const intervalString = parseInterval(recurring_interval);

  if (recurring_interval_count > 1) {
    label += `${recurring_interval_count} ${intervalString}`;
    if (recurring_interval == "month") {
      label += "es";
    } else {
      label += "s";
    }
  } else {
    label += intervalString;
  }

  return label;
};

export const parseInterval = (interval: RecurringInterval): string => {
  switch (interval) {
    case "week":
      return "semana";
    case "month":
      return "mes";
    case "year":
      return "año";
    default:
      return "";
  }
};

export const pricePerRecurrence = (price: Price, currency: Currency) => {
  const pricePer = toString({
    amount: price.amount / normalizeInterval(price),
    currency: currency,
  });

  return `${pricePer}/mes`;
};

export const normalizeInterval = (price: Price): number => {
  const rawCount = price.recurring_interval_count;

  if (price.recurring_interval === "year") {
    return rawCount * 12;
  } else if (price.recurring_interval === "month") {
    return rawCount;
  } else if (price.recurring_interval === "week") {
    return rawCount / 4.34524;
  } else if (price.recurring_interval === "day") {
    return rawCount / 30;
  }
};

export const toYearlyAmmount = (price: Price) => {
  // get yearly equivalent of price
  switch (price.recurring_interval) {
    case "month":
      return (price.amount / price.recurring_interval_count) * 12;
    case "year":
      return price.amount / price.recurring_interval_count;
    case "week":
      return (price.amount / price.recurring_interval_count) * 52.1429; // weeks in a year
    case "day":
      return (price.amount / price.recurring_interval_count) * 365; // weeks in a year
  }
};

export const getSavings = (worsePrice: Price, currentPrice: Price) => {
  const worsePriceYearlyAmmount = toYearlyAmmount(worsePrice);
  const currentPriceYearlyAmmount = toYearlyAmmount(currentPrice);

  // returns % saved without % sign
  return Math.trunc(
    ((worsePriceYearlyAmmount - currentPriceYearlyAmmount) /
      worsePriceYearlyAmmount) *
      100
  );
};

export const getWorstPrice = (prices) => {
  // typescript strict does not allow for modifying arrays in place so we slice to clone
  return prices.slice().sort((a: Price, b: Price) => {
    return a.amount - b.amount;
  })[0];
};

export const isBestPrice = (prices: Price[], price: Price) => {
  const worsePrice = getWorstPrice(prices);
  const savedAmmount = prices
    .slice()
    .sort((a, b) => getSavings(worsePrice, b) - getSavings(worsePrice, a));
  return savedAmmount[0].amount === price.amount;
};

export const getSavingsCash = (mostExpensive: Price, price: Price): number => {
  return mostExpensive.amount - price.amount;
};

export const getPayInXInstallmentsMessage = (
  price: Price,
  merchant: Merchant
) => {
  // generates message "x pagos de x cada x"
  const installmentCount = price.installments_count;
  const singleInstallmentAmount = price.amount / installmentCount;
  const singleInstallmentAsCurrency = amountAsCurrency({
    amount: singleInstallmentAmount,
    currency: merchant.currency_code,
  });
  return `${installmentCount} pagos de ${singleInstallmentAsCurrency} ${priceIntervalText(
    price
  )}`;
};

export const singleInstallmentAsCurrency = (
  price: Price,
  merchant: Merchant
) => {
  const singleInstallmentAmount = price.amount / price.installments_count;
  return toString({
    amount: singleInstallmentAmount,
    currency: merchant.currency_code,
  });
};

// returns raw amount for recurrences and one_offs,
// and amount divided by installments_count for installments
export const amountPerInstallment = (price: Price) => {
  const { amount, installments_count, payment_plan_type } = price;

  const amt =
    payment_plan_type === PaymentPlanType.INSTALLMENT
      ? amount / installments_count
      : amount;

  return amt;
};
